import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';
import { NOTIFICATIONS_QUERY_KEY } from './use-notifications';

/**
 * `useMarkRead` (W6-F4-S1) — POSTs `/api/v1/proxy/core/notifications/{id}/read`.
 * Optimistically clears the `read_at` field locally so the badge clears
 * immediately, then refetches on settle.
 */
export function useMarkRead(): UseMutationResult<void, Error, string> {
  const { getAccessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await apiFetch<{ ok: true }>(
        `/api/v1/proxy/core/notifications/${encodeURIComponent(id)}/read`,
        {
          method: 'POST',
          getAccessToken,
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        },
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });
}

export function useMarkAllRead(): UseMutationResult<{ count: number }, Error, void> {
  const { getAccessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<{ count: number }, Error, void>({
    mutationFn: async () => {
      const res = await apiFetch<{ ok: true; count: number }>(
        `/api/v1/proxy/core/notifications/mark-all-read`,
        {
          method: 'POST',
          getAccessToken,
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        },
      );
      return { count: res.count };
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });
}
